/* components/patterns/LeftVisual.jsx — левая визуальная панель auth-сплита.
   ПОРТ страницы из старого фронта (frontend/src/components/auth/LeftVisual.jsx):
   редакторский заголовок + ЖИВОЙ анимированный чат-демо (печатает запрос → стримит ответ),
   циклит 5 функций Ресториума + чипы переключения. Светлая обложка. Скрыта на ≤720 (CSS).
   Адаптив под 5 разрешений — через auth.css (.auth-cover-stage). */

import { useCallback, useEffect, useState } from 'react';
import { Icon } from '../ui/Icon.jsx';

const LOGO_MARK = '/logo-mark.svg';

// Каждый диалог — превью одной функции Ресториума (формат ответа повторяет реальные промпты):
//   ABC → таблица приоритетов; Фудкост → варианты по мерам; ТТК → сырьё БРУТТО/НЕТТО/ВЫХОД,
//   КБЖУ, аллергены; Меню → проект-меню с FC; Инвентаризация → ТОП недостач.
const CHAT_SCRIPT = [
  {
    feature: 'abc',
    label: 'ABC',
    user: 'Загрузил ABC по меню за апрель. Что усилить, что убрать?',
    ai:
`ABC · 87 позиций, апрель:

❗ Рибай 350 г — AAA, FC 27% · тест цены +120 ₽
🛑 Утиное конфи — CCC, FC 31% · снять с меню
⚠️ Сёмга с пюре — ACC, FC 24% · пересобрать гарнир
📈 Карбонара — BBB, FC 17% · в фокус витрины

Раскрыть таблицу по всем 87 позициям?`,
  },
  {
    feature: 'foodcost',
    label: 'Фудкост',
    user: 'Сократи фудкост по тарелке «лосось / спаржа» на 18%. Подача та же.',
    ai:
`Фудкост · текущий 26% → цель 21,3%:

• Соус 35 → 22 г · −14 ₽ · риск низкий
• Лосось → стейк форели · −61 ₽ · риск низкий
• Спаржа → черемша · −42 ₽ · риск низкий

Безопасный микс 1+3: −19,4% к себестоимости.
Открыть пересобранную ТТК?`,
  },
  {
    feature: 'ttk',
    label: 'ТТК',
    user: 'Собери ТТК по ГОСТу: ризотто с белыми грибами, выход 220 г.',
    ai:
`ТТК-067 · Ризотто с белыми грибами, 220 г:

• Карнароли   85 / 70 г (потери 18%)
• Белый гриб  62 / 55 г · Бульон 110 мл
• Пармезан    19 / 18 г · Сл. масло 12 г

КБЖУ 312 / 8 / 14 / 38 · Аллергены: глютен, молоко.
Срок 12 ч, +2…+4 °C. Экспорт в Word?`,
  },
  {
    feature: 'menu',
    label: 'Меню',
    user: 'Проект-меню для бистро «Север», чек 1 800 ₽, FC 28%.',
    ai:
`Проект-меню · бистро «Север», FC-цель 28%:

• Строганина из муксуна — 690 ₽, FC 24%
• Томлёный судак, каша из полбы — 1 240 ₽, FC 27%
• Каре ягнёнка с тыквенным мисо — 1 940 ₽, FC 29%
• Морошка-десерт, кедровый крем — 480 ₽, FC 22%

Медиана FC 26%. Список ПФ и план проработки?`,
  },
  {
    feature: 'inventory',
    label: 'Инвентаризация',
    user: 'Сравни инвентаризацию март/апрель. Где главные дыры?',
    ai:
`Инвентаризация · апрель, ТОП-5 недостач:

• Сл. масло 82,5% −4,2 кг (−2 940 ₽) · пересорт
• Лосось ASC −1,8 кг (−2 160 ₽) · разрыв ТТК
• Пармезан −0,9 кг (−2 250 ₽) · перекрёст с баром
• Спаржа −1,1 кг (−880 ₽) · стаф / проработки
• Креветки −0,6 кг (−1 440 ₽) · Факт=0, пересчёт

Прогноз: −58% к недостаче. Перейти к 13 критериям?`,
  },
];

function Reveal({ open, children }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateRows: open ? '1fr' : '0fr',
      opacity: open ? 1 : 0,
      transition: 'grid-template-rows .42s cubic-bezier(.2,.7,.2,1), opacity .28s ease',
    }}>
      <div style={{ overflow: 'hidden', minHeight: 0 }}>{children}</div>
    </div>
  );
}

function TypingDots() {
  return (
    <div style={dotsStyles.row}>
      <span style={{ ...dotsStyles.dot, animationDelay: '0s' }} />
      <span style={{ ...dotsStyles.dot, animationDelay: '.15s' }} />
      <span style={{ ...dotsStyles.dot, animationDelay: '.3s' }} />
    </div>
  );
}

const dotsStyles = {
  row: { display: 'flex', gap: 5, alignItems: 'center' },
  dot: {
    width: 6, height: 6, borderRadius: '50%',
    background: 'var(--text-mute)',
    animation: 'chat-dot 1s ease-in-out infinite',
  },
};

function ChatDemo({ exchangeIdx, onAdvance }) {
  const [stage, setStage] = useState('idle');
  const [userText, setUserText] = useState('');
  const [aiText, setAiText] = useState('');

  const exchange = CHAT_SCRIPT[exchangeIdx];

  useEffect(() => {
    setStage('idle');
    setUserText('');
    setAiText('');
  }, [exchangeIdx]);

  useEffect(() => {
    let timer;
    if (stage === 'idle') {
      timer = setTimeout(() => setStage('typing-input'), 700);
    } else if (stage === 'typing-input') {
      if (userText.length < exchange.user.length) {
        const ch = exchange.user[userText.length];
        let delay = 22 + Math.random() * 26;
        if (ch === ' ') delay += 10;
        if (',.?!:'.includes(ch)) delay += 70;
        timer = setTimeout(
          () => setUserText(exchange.user.slice(0, userText.length + 1)),
          delay,
        );
      } else {
        timer = setTimeout(() => setStage('sent'), 700);
      }
    } else if (stage === 'sent') {
      timer = setTimeout(() => setStage('thinking'), 500);
    } else if (stage === 'thinking') {
      timer = setTimeout(() => setStage('responding'), 1400);
    } else if (stage === 'responding') {
      if (aiText.length < exchange.ai.length) {
        const ch = exchange.ai[aiText.length];
        let delay = 10 + Math.random() * 16;
        if (ch === ' ') delay += 6;
        if (ch === '\n') delay += 80;
        if (',.?!:'.includes(ch)) delay += 90;
        timer = setTimeout(
          () => setAiText(exchange.ai.slice(0, aiText.length + 1)),
          delay,
        );
      } else {
        timer = setTimeout(() => setStage('done'), 200);
      }
    } else if (stage === 'done') {
      timer = setTimeout(() => onAdvance(), 2200);
    }
    return () => clearTimeout(timer);
  }, [stage, userText, aiText, exchange.user, exchange.ai, onAdvance]);

  const composerText = stage === 'typing-input' ? userText : '';
  const showComposerCursor = stage === 'typing-input' || stage === 'idle';

  const userBubbleOpen =
    stage === 'sent' || stage === 'thinking' ||
    stage === 'responding' || stage === 'done';
  const userBubbleText = userBubbleOpen ? exchange.user : '';

  const thinkingOpen = stage === 'thinking';
  const aiOpen = stage === 'responding' || stage === 'done';
  const aiStreaming = stage === 'responding';
  const sendActive = composerText.length > 0;
  const streamOpen = userBubbleOpen || thinkingOpen || aiOpen;

  return (
    <div style={chatDemoStyles.card}>
      <div style={chatDemoStyles.head}>
        <div style={chatDemoStyles.headLeft}>
          <div style={chatDemoStyles.headAvatar}>
            <img src={LOGO_MARK} alt="" style={{ width: 18, height: 18 }} />
          </div>
          <div style={chatDemoStyles.headText}>
            <div style={chatDemoStyles.headName}>Аргус</div>
            <div style={chatDemoStyles.headSub}>ИИ-ассистент Ресториума</div>
          </div>
        </div>
        <div style={chatDemoStyles.headRight}>
          <span style={chatDemoStyles.statusDot} />
          <span style={chatDemoStyles.statusLabel}>онлайн</span>
        </div>
      </div>

      <div style={{
        ...chatDemoStyles.stream,
        paddingBottom: streamOpen ? 12 : 0,
      }}>
        <Reveal open={userBubbleOpen}>
          <div style={chatDemoStyles.userRow}>
            <div style={chatDemoStyles.bubbleUser}>{userBubbleText}</div>
          </div>
        </Reveal>

        <Reveal open={thinkingOpen}>
          <div style={{ ...chatDemoStyles.aiRow, marginTop: 10 }}>
            <div style={chatDemoStyles.aiAvatar}>
              <img src={LOGO_MARK} alt="" style={{ width: 16, height: 16 }} />
            </div>
            <div style={chatDemoStyles.bubbleAiTyping}>
              <TypingDots />
            </div>
          </div>
        </Reveal>

        <Reveal open={aiOpen}>
          <div style={{ ...chatDemoStyles.aiRow, marginTop: 10 }}>
            <div style={chatDemoStyles.aiAvatar}>
              <img src={LOGO_MARK} alt="" style={{ width: 16, height: 16 }} />
            </div>
            <div style={chatDemoStyles.bubbleAi}>
              <span style={chatDemoStyles.streamText}>{aiText}</span>
              {aiStreaming && <span style={chatDemoStyles.streamCaret} />}
            </div>
          </div>
        </Reveal>
      </div>

      <div style={chatDemoStyles.composer}>
        <div style={chatDemoStyles.composerText}>
          {composerText ? (
            <span style={chatDemoStyles.composerValue}>{composerText}</span>
          ) : (
            <span style={chatDemoStyles.composerPlaceholder}>Спросите Аргуса…</span>
          )}
          {showComposerCursor && <span style={chatDemoStyles.composerCaret} />}
        </div>
        <button type="button" aria-label="Голосовой ввод" tabIndex={-1} style={chatDemoStyles.micBtn}>
          <Icon name="microphone" size={14} />
        </button>
        <button
          type="button"
          aria-label="Отправить"
          tabIndex={-1}
          style={{
            ...chatDemoStyles.sendBtn,
            background: sendActive ? 'var(--accent)' : 'var(--surface-2)',
            color: sendActive ? '#fff' : 'var(--text-mute)',
            boxShadow: sendActive ? '0 6px 18px -6px var(--accent-glow)' : 'none',
          }}
        >
          <Icon name="paper-plane" size={14} />
        </button>
      </div>
    </div>
  );
}

const chatDemoStyles = {
  card: {
    position: 'relative', zIndex: 2,
    maxHeight: 384,
    overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(255,255,255,.94), rgba(248,249,253,.94))',
    border: '1px solid var(--border)', borderRadius: 16,
    padding: 14, display: 'flex', flexDirection: 'column',
    boxShadow: '0 30px 80px rgba(15,15,30,.14), 0 0 0 1px rgba(15,15,30,.02) inset',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
  },
  head: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    paddingBottom: 12, borderBottom: '1px solid var(--border)', gap: 12,
    marginBottom: 12,
  },
  headLeft: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  headAvatar: {
    width: 32, height: 32, borderRadius: 9, flexShrink: 0,
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    display: 'grid', placeItems: 'center',
    boxShadow: '0 0 0 3px rgba(55,118,254,.06)',
  },
  headText: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
  headName: { fontSize: 13, fontWeight: 600, color: 'var(--text)', letterSpacing: '.005em' },
  headSub: {
    fontSize: 11, color: 'var(--text-mute)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  headRight: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontFamily: 'Geist Mono, ui-monospace, monospace',
    fontSize: 10, color: 'var(--text-mute)',
    letterSpacing: '.08em', textTransform: 'uppercase',
    flexShrink: 0,
  },
  statusDot: {
    width: 6, height: 6, borderRadius: '50%', background: 'var(--success)',
    boxShadow: '0 0 8px rgba(28,159,98,.45)',
  },
  statusLabel: { color: 'var(--text-mute)' },
  stream: {
    minHeight: 0, overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    transition: 'padding-bottom .42s cubic-bezier(.2,.7,.2,1)',
  },
  userRow: { display: 'flex', justifyContent: 'flex-end', paddingBottom: 2 },
  bubbleUser: {
    maxWidth: '85%', background: 'var(--accent)',
    color: '#fff', fontSize: 12.5, lineHeight: 1.45,
    padding: '9px 13px',
    borderRadius: '14px 14px 4px 14px',
    whiteSpace: 'pre-wrap',
  },
  aiRow: { display: 'flex', gap: 8, alignItems: 'flex-start', paddingBottom: 2 },
  aiAvatar: {
    width: 26, height: 26, borderRadius: 7, flexShrink: 0,
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    display: 'grid', placeItems: 'center',
  },
  bubbleAi: {
    maxWidth: 'calc(100% - 34px)',
    background: 'var(--surface-2)',
    color: 'var(--text)', fontSize: 12.5, lineHeight: 1.55,
    padding: '11px 13px', border: '1px solid var(--border)',
    borderRadius: '14px 14px 14px 4px',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  },
  bubbleAiTyping: {
    background: 'var(--surface-2)', padding: '12px 14px',
    border: '1px solid var(--border)',
    borderRadius: '14px 14px 14px 4px',
  },
  streamText: { whiteSpace: 'pre-wrap' },
  streamCaret: {
    display: 'inline-block', marginLeft: 2,
    width: 7, height: 13, verticalAlign: '-2px',
    background: 'var(--accent)',
    animation: 'blink 1s steps(2,end) infinite',
  },
  composer: {
    flexShrink: 0,
    display: 'flex', alignItems: 'flex-end', gap: 10,
    padding: '10px 12px', border: '1px solid var(--border)',
    borderRadius: 12, background: 'rgba(15,15,30,.03)',
    transition: 'border-color .2s',
  },
  composerText: {
    flex: 1, minHeight: 22,
    color: 'var(--text)', fontSize: 13, lineHeight: 1.5,
    fontFamily: 'inherit',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap',
  },
  composerValue: { color: 'var(--text)' },
  composerPlaceholder: { color: 'var(--text-mute)' },
  composerCaret: {
    display: 'inline-block', marginLeft: 1,
    width: 1.5, height: 14,
    background: 'var(--accent)',
    animation: 'blink 1s steps(2,end) infinite',
    alignSelf: 'center',
  },
  sendBtn: {
    appearance: 'none', border: 0, cursor: 'default',
    width: 30, height: 30, borderRadius: 8,
    display: 'grid', placeItems: 'center',
    transition: 'background .2s, color .2s, box-shadow .2s',
    flexShrink: 0,
  },
  micBtn: {
    appearance: 'none', border: 0, cursor: 'default',
    width: 30, height: 30, borderRadius: 8,
    display: 'grid', placeItems: 'center',
    background: 'transparent',
    color: 'var(--text-mute)',
    transition: 'background .2s, color .2s',
    flexShrink: 0,
  },
};

function FeatureChips({ activeIdx, onSelect }) {
  return (
    <div style={chipStyles.row} role="tablist" aria-label="Возможности Ресториума">
      {CHAT_SCRIPT.map((item, idx) => {
        const isActive = idx === activeIdx;
        return (
          <button
            key={item.feature}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(idx)}
            style={{ ...chipStyles.chip, ...(isActive ? chipStyles.chipActive : null) }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

const chipStyles = {
  row: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
    justifyContent: 'center', paddingTop: 4,
  },
  chip: {
    appearance: 'none',
    padding: '5px 10px',
    borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--border)',
    borderRadius: 999,
    fontFamily: 'Geist Mono, ui-monospace, monospace',
    fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase',
    color: 'var(--text-mute)',
    background: 'rgba(255,255,255,.7)',
    cursor: 'pointer',
    transition: 'background .35s, color .35s, border-color .35s, box-shadow .35s',
  },
  chipActive: {
    color: 'var(--text)',
    background: 'rgba(55,118,254,.10)',
    borderColor: 'rgba(55,118,254,.35)',
    boxShadow: '0 0 0 3px rgba(55,118,254,.06)',
  },
};

function LeftVisual() {
  const [exchangeIdx, setExchangeIdx] = useState(0);
  const handleAdvance = useCallback(
    () => setExchangeIdx((i) => (i + 1) % CHAT_SCRIPT.length),
    [],
  );
  return (
    <div className="auth-cover-stage" style={styles.root}>
      <div style={styles.gridBg}>
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} style={styles.gridLine} />
        ))}
      </div>
      <div style={styles.glow} />

      <div style={styles.center}>
        <div style={styles.stage}>
          <div style={styles.headline}>
            <div style={styles.kicker}>Для шефов и управляющих</div>
            <h1 style={styles.title}>
              От закупки<br />
              <em style={styles.titleItalic}>до подачи —</em><br />
              <span style={styles.titleAccent}>одним сообщением.</span>
            </h1>
          </div>

          <div style={styles.chatWrap}>
            <ChatDemo exchangeIdx={exchangeIdx} onAdvance={handleAdvance} />
          </div>

          <FeatureChips activeIdx={exchangeIdx} onSelect={setExchangeIdx} />
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: {
    position: 'relative', height: '100%', width: '100%',
    background: 'radial-gradient(110% 60% at 25% 5%, #F3F5FC 0%, #EAEDF4 55%, #E1E4EC 100%)',
    padding: '32px 44px', display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  gridBg: {
    position: 'absolute', inset: 0, display: 'grid',
    gridTemplateColumns: 'repeat(12, 1fr)',
    pointerEvents: 'none', opacity: 0.5,
  },
  gridLine: { borderRight: '1px solid rgba(15,15,30,.04)' },
  glow: {
    position: 'absolute', left: '50%', top: '40%',
    transform: 'translate(-50%, -50%)',
    width: 540, height: 540, borderRadius: '50%',
    background: 'radial-gradient(circle, var(--accent-glow) 0%, rgba(255,255,255,0) 60%)',
    filter: 'blur(24px)', opacity: 0.45, pointerEvents: 'none',
  },
  center: {
    position: 'relative', zIndex: 2, flex: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 0,
  },
  stage: {
    width: '100%', maxWidth: 540,
    display: 'flex', flexDirection: 'column', gap: 22,
  },
  headline: { position: 'relative' },
  kicker: {
    display: 'inline-block',
    padding: '5px 11px',
    border: '1px solid var(--border)',
    borderRadius: 999,
    fontFamily: 'Geist Mono, ui-monospace, monospace',
    fontSize: 10.5, letterSpacing: '.08em',
    textTransform: 'uppercase',
    color: 'var(--text-dim)', marginBottom: 14,
  },
  title: {
    fontFamily: "'PT Serif', Georgia, serif",
    fontWeight: 400, fontSize: 'clamp(36px, 4vw, 60px)',
    lineHeight: 1.02, letterSpacing: '-0.02em',
    color: 'var(--text)', margin: 0,
  },
  titleItalic: { fontStyle: 'italic', color: 'var(--text-dim)' },
  titleAccent: { color: 'var(--accent)' },
  chatWrap: { position: 'relative', width: '100%' },
};

export { LeftVisual };
